import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useTheme } from 'context/ThemeContext';
import SolarIcon from 'components/icons/SolarIcon';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import type { ComingDest, ComingObjectOption } from '../model/comingObject';

interface DestSelectOption {
    value: ComingDest;
    label: string;
    icon: 'home' | 'building' | 'lock';
}

interface ComingDestSelectProps {
    dest: ComingDest;
    destOptions: ComingDest[];
    selectedObject?: ComingObjectOption | null;
    disabled?: boolean;
    onSelect: (dest: ComingDest) => void;
}

const DEST_ICONS: Record<ComingDest, DestSelectOption['icon']> = {
    main: 'home',
    object: 'building',
    reserve: 'lock',
};

const ComingDestSelect = ({
    dest,
    destOptions,
    selectedObject = null,
    disabled = false,
    onSelect,
}: ComingDestSelectProps) => {
    const { t } = useTranslation(['Vendors']);
    const { darkTheme } = useTheme();
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    const selectOptions = useMemo<DestSelectOption[]>(() => {
        const isSale = Number(selectedObject?.onlySale);
        const labels: Record<ComingDest, string> = {
            main: t('supplierRequests.view.destMain'),
            object: isSale
                ? t('supplierRequests.view.destSale')
                : t('supplierRequests.view.destObject'),
            reserve: t('supplierRequests.view.destReserve'),
        };
        return destOptions.map((value) => ({
            value,
            label: labels[value],
            icon: DEST_ICONS[value],
        }));
    }, [destOptions, selectedObject?.onlySale, t]);

    const value = selectOptions.find((option) => option.value === dest) || selectOptions[0] || null;

    const styles = {
        ...selectTheme,
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 1060 }),
    };

    return (
        <div className="mb-3">
            <label className="form-label text-muted">
                {t('supplierRequests.view.selectDest')}
            </label>
            <Select<DestSelectOption>
                options={selectOptions}
                value={value}
                isDisabled={disabled || selectOptions.length <= 1}
                isClearable={false}
                isSearchable={false}
                styles={styles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                formatOptionLabel={(option) => (
                    <span className="d-flex align-items-center gap-2">
                        <SolarIcon name={option.icon} className="flex-shrink-0 fs-16" />
                        <span>{option.label}</span>
                    </span>
                )}
                onChange={(option) => {
                    if (option) onSelect(option.value);
                }}
            />
        </div>
    );
};

export default ComingDestSelect;
